/**
 * Start a BAM Certification checkout. Shared by the public cert page
 * (#/certification) and the direct checkout page (#/buy-cert).
 *
 * Signed in is optional: with a session the cert lands on that account;
 * without one Stripe collects the email and the webhook makes the account
 * after payment. Either way the Meta browser ids and the ad source travel
 * with the request so the sale can be matched to the ad.
 *
 * plan: "full" ($1,399, card or Klarna) or "3pay" (3 monthly payments).
 * code: an optional cert discount code, checked server-side.
 * Returns { url } to send the browser to, or { error }.
 */
import { supabase, SUPABASE_ANON_KEY } from "../../supabaseClient";
import { getMetaContext } from "../../utils/metaPixel";
import { getAdSource } from "../../utils/adSource";
import { assertNotStaging } from "../../config/environment";

export const CERT_PRICE = 1399;
export const CERT_3PAY_AMOUNT = 499;
export const CERT_3PAY_COUNT = 3;
// International coach pricing: $400 off outside the US, no code needed.
export const INTL_DISCOUNT = 400;
export const INTL_PRICE = CERT_PRICE - INTL_DISCOUNT;       // $999
export const INTL_3PAY_AMOUNT = 333;                         // 3 x $333 = $999

/** The prices this visitor sees. `country` comes from api/geo; unknown means US pricing. */
export function certPricing(country) {
  const intl = !!country && country !== "US";
  return intl
    ? { intl, full: INTL_PRICE, wasFull: CERT_PRICE, pay: INTL_3PAY_AMOUNT, wasPay: CERT_3PAY_AMOUNT, saving: INTL_DISCOUNT }
    : { intl, full: CERT_PRICE, wasFull: null, pay: CERT_3PAY_AMOUNT, wasPay: null, saving: 0 };
}

/** The visitor's country code from the edge (api/geo), or null. */
export async function fetchCountry() {
  try {
    const r = await fetch("/api/geo", { cache: "no-store" });
    const d = await r.json();
    return typeof d?.country === "string" ? d.country : null;
  } catch { return null; }
}

const CHECKOUT_URL =
  process.env.REACT_APP_CERT_CHECKOUT_URL ||
  "https://rlfekxctjyvozujzbzwm.supabase.co/functions/v1/create-cert-checkout";

export async function startCertCheckout({ plan = "full", returnHash = "#/certification", code = "", country = null } = {}) {
  const intl = !String(code || "").trim() && !!country && country !== "US";
  try {
    assertNotStaging("Checkout");
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const origin = window.location.origin;
    // {CHECKOUT_SESSION_ID} is filled in by Stripe. The page uses it as the
    // Meta event id so the browser and server Purchase count once.
    const successUrl = token
      ? `${origin}/?cert_purchased=1&plan=${plan}${intl ? "&intl=1" : ""}&sid={CHECKOUT_SESSION_ID}#/certified`
      : `${origin}/?cert_purchased=1&guest=1&plan=${plan}${intl ? "&intl=1" : ""}&sid={CHECKOUT_SESSION_ID}#/certification`;
    const headers = { "Content-Type": "application/json" };
    // The functions gateway wants a JWT either way: the coach's session, or
    // for a signed-out visitor the public anon key (the function reads that
    // as a guest).
    headers.Authorization = `Bearer ${token || SUPABASE_ANON_KEY}`;
    headers.apikey = SUPABASE_ANON_KEY;
    const res = await fetch(CHECKOUT_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({
        plan,
        code: String(code || "").trim(),
        intl,
        country: country || undefined,
        successUrl,
        cancelUrl: `${origin}/?cert_cancelled=1${returnHash}`,
        meta: getMetaContext(),
        utm: getAdSource(),
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.url) return { error: data.error || "Couldn't start checkout. Try again." };
    return { url: data.url, alreadyPurchased: !!data.already_purchased };
  } catch (e) {
    return { error: e?.message || "Network error. Try again." };
  }
}
